import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Eye, CheckCircle, XCircle, FileText, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

export const FinanceHeadQueuePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const data = await vouchersApi.getAll();
      // Filter vouchers assigned to this Finance Head (as manager) in SUBMITTED status
      const assignedVouchers = data.filter(
        v => v.assignedManagerId === user?.id && v.status === 'SUBMITTED'
      );
      setVouchers(assignedVouchers);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-head-queue-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">My Approval Queue</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Vouchers assigned to you for Finance Head approval
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Pending Approvals ({vouchers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vouchers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No vouchers pending your approval</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Voucher No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Beneficiary</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vouchers.map((voucher) => (
                  <TableRow key={voucher.id} data-testid={`voucher-row-${voucher.id}`}>
                    <TableCell className="font-mono font-medium">
                      <div className="flex items-center gap-1">
                        {voucher.voucherNo}
                        {voucher.attachments && voucher.attachments.length > 0 && (
                          <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(voucher.date)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{voucher.beneficiaryName}</p>
                        <p className="text-xs text-muted-foreground">{voucher.createdByName}</p>
                      </div>
                    </TableCell>
                    <TableCell>{voucher.department}</TableCell>
                    <TableCell className="font-mono">
                      {formatCurrency(voucher.amountTotal, voucher.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={voucher.status} size="sm" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/finance-head/vouchers/${voucher.id}`)}
                        data-testid={`process-voucher-${voucher.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Process
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinanceHeadQueuePage;
